import * as React from 'react';
import { Plus, Trash2, Save, AlertTriangle, Activity, ShieldAlert, Info, Edit, X } from 'lucide-react';
import { PhysiologicalValidationService, ValidationAlert } from '../services/physiologicalValidationService';
import { UnifiedEMGResults } from '../types/emg';

// Tipos de datos para EMG
interface EMGMotorUnitPotential {
  amplitude: number; // μV
  duration: number;  // ms
  polyphasia: number; // %
  amplitudeLabel?: string;
  durationLabel?: string;
  polyphasiaLabel?: string;
  configuration?: 'normal' | 'polyphasic' | 'serrated' | 'unstable' | 'few' | 'none' | '';
  phases?: number;
  turns?: number;
  riseTimeUs?: number;
}

interface EMGSpontaneousActivity {
  fibrillations: 0 | 1 | 2 | 3 | 4;
  positiveWaves: 0 | 1 | 2 | 3 | 4;
  fasciculations: boolean;
  crd: boolean;
  myotonicDischarges?: boolean;
}

export interface EMGMaxVolitionalActivity {
  amplitude?: 'normal' | 'reduced' | 'increased' | 'absent' | '';
  pattern?: 'full' | 'reduced' | 'discrete' | 'early' | 'absent' | '';
  effort?: 'max' | 'submax' | 'poor' | '';
}

export interface EMGNerveRecord {
  id: string; // ID único para esta entrada de registro
  muscleOrNerveName: string; // Nombre del músculo o nervio evaluado
  side: 'left' | 'right';
  insertionalActivity: 'normal' | 'increased' | 'decreased' | 'absent' | '';
  spontaneousActivity: EMGSpontaneousActivity;
  motorUnitPotentials: EMGMotorUnitPotential;
  recruitmentPattern: 'normal' | 'reduced_incomplete' | 'reduced_complete' | 'early' | 'discrete' | '';
  maxVolitionalActivity?: EMGMaxVolitionalActivity;
  rawTableRow?: string[];
  extractionSource?: {
    parser?: 'table' | 'narrative' | 'ai' | 'legacy';
    sourceSection?: string;
    tableIndex?: number;
    rowIndex?: number;
    evidence?: string;
    decisionTrace?: string[];
  };
  interpretationNotes?: string; // Notas opcionales para este registro específico
}

interface EMGNeedleAnalysisProps {
  onComplete: (data: EMGNerveRecord[]) => void; // Actualizado para enviar un array de registros
  initialData?: EMGNerveRecord[]; // 🔥 NUEVO: Soporte para datos iniciales
}

// Lista simplificada de músculos/nervios para EMG. Esto podría venir de un archivo de datos.
const emgMusclesDatabase: { id: string; name: string }[] = [
  { id: 'deltoid', name: 'Deltoides' },
  { id: 'biceps_brachii', name: 'Bíceps Braquial' },
  { id: 'triceps_brachii', name: 'Tríceps Braquial' },
  { id: 'first_dorsal_interosseous', name: '1er Interóseo Dorsal (Mano)' },
  { id: 'abductor_pollicis_brevis', name: 'Abductor Corto del Pulgar (APB)' },
  { id: 'tibialis_anterior', name: 'Tibial Anterior' },
  { id: 'gastrocnemius_medial', name: 'Gastrocnemio Medial' },
  { id: 'vastus_medialis', name: 'Vasto Medial' },
  { id: 'lumbar_paraspinals', name: 'Paraespinales Lumbares' },
];

const initialCurrentRecordState: Partial<EMGNerveRecord> = {
  muscleOrNerveName: '',
  side: 'right',
  insertionalActivity: '',
  spontaneousActivity: {
    fibrillations: 0,
    positiveWaves: 0,
    fasciculations: false,
    crd: false,
  },
  motorUnitPotentials: {
    amplitude: 0,
    duration: 0,
    polyphasia: 0,
    amplitudeLabel: '',
    durationLabel: '',
    polyphasiaLabel: '',
    configuration: '',
    phases: 0,
    turns: 0,
    riseTimeUs: 0,
  },
  recruitmentPattern: '',
  maxVolitionalActivity: {
    amplitude: '',
    pattern: '',
    effort: '',
  },
  interpretationNotes: '',
};

const formatMupValue = (value: number, label: string | undefined, unit: string) => {
  if (label) return label;
  return `${value} ${unit}`;
};

const EMGNeedleAnalysis: React.FC<EMGNeedleAnalysisProps> = ({ onComplete, initialData }) => {
  const [emgRecords, setEmgRecords] = React.useState<EMGNerveRecord[]>([]);
  const [currentRecord, setCurrentRecord] = React.useState<Partial<EMGNerveRecord>>(initialCurrentRecordState);
  const [editingRecordId, setEditingRecordId] = React.useState<string | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});
  const [emgValidationAlerts, setEmgValidationAlerts] = React.useState<ValidationAlert[]>([]);
  const [currentEmgValidationAlerts, setCurrentEmgValidationAlerts] = React.useState<ValidationAlert[]>([]);

  // 🔥 Auto-fill effect para cargar datos iniciales EMG
  React.useEffect(() => {
    if (initialData) {
      console.log('🔬 EMGNeedleAnalysis: Cargando datos iniciales...', initialData);
      console.log('🔍 Estructura de initialData EMG:', {
        hasInitialData: !!initialData,
        isArray: Array.isArray(initialData),
        length: Array.isArray(initialData) ? initialData.length : 0,
        dataType: typeof initialData
      });
      
      if (Array.isArray(initialData) && initialData.length > 0) {
        console.log(`📊 Auto-llenando ${initialData.length} registros EMG`);
        
        // Verificar estructura de cada registro EMG
        initialData.forEach((emg, index) => {
          console.log(`   EMG ${index + 1}:`, {
            id: emg.id,
            muscleOrNerveName: emg.muscleOrNerveName,
            side: emg.side,
            insertionalActivity: emg.insertionalActivity,
            hasRequiredFields: !!(emg.id && emg.muscleOrNerveName && emg.side)
          });
        });
        
        // Cargar registros EMG existentes
        setEmgRecords(initialData);
        
        console.log('✅ EMGNeedleAnalysis: Datos iniciales cargados exitosamente');
        console.log('🎯 Registros EMG cargados:', initialData.map(record => 
          `${record.muscleOrNerveName} (${record.side})`
        ).join(', '));
        
        // 🔥 FORZAR RE-RENDER para asegurar que la lista se actualice
        setTimeout(() => {
          console.log('🔄 Estado actual de emgRecords:', emgRecords.length);
          console.log('🔄 Estado actual de setEmgRecords después de timeout:', initialData.length);
        }, 100);
      } else {
        console.warn('⚠️ No hay datos EMG válidos para cargar:', {
          exists: !!initialData,
          isArray: Array.isArray(initialData),
          length: Array.isArray(initialData) ? initialData.length : 0
        });
      }
    } else {
      console.warn('⚠️ EMGNeedleAnalysis: No se recibieron datos iniciales');
    }
  }, [initialData]);

  const handleInputChange = (field: keyof EMGNerveRecord, value: any) => {
    setCurrentRecord(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNestedInputChange = (
    section: 'spontaneousActivity' | 'motorUnitPotentials',
    field: keyof EMGSpontaneousActivity | keyof EMGMotorUnitPotential,
    value: any
  ) => {
    setCurrentRecord(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as any),
        [field]: value,
      },
    }));
  };

  const validateCurrentRecord = (): boolean => {
    const errors: Record<string, string> = {};
    if (!currentRecord.muscleOrNerveName) errors.muscleOrNerveName = 'Seleccione un músculo/nervio.';
    if (!currentRecord.insertionalActivity) errors.insertionalActivity = 'Seleccione actividad de inserción.';
    if (currentRecord.motorUnitPotentials?.amplitude === undefined || currentRecord.motorUnitPotentials.amplitude < 0) errors.mup_amplitude = 'Amplitud PUM inválida.';
    if (currentRecord.motorUnitPotentials?.duration === undefined || currentRecord.motorUnitPotentials.duration < 0) errors.mup_duration = 'Duración PUM inválida.';
    if (currentRecord.motorUnitPotentials?.polyphasia === undefined || currentRecord.motorUnitPotentials.polyphasia < 0 || currentRecord.motorUnitPotentials.polyphasia > 100) errors.mup_polyphasia = 'Polifasia PUM inválida (0-100%).';
    if (currentRecord.motorUnitPotentials?.phases !== undefined && currentRecord.motorUnitPotentials.phases < 0) errors.mup_phases = 'Fases inválidas.';
    if (currentRecord.motorUnitPotentials?.turns !== undefined && currentRecord.motorUnitPotentials.turns < 0) errors.mup_turns = 'Giros inválidos.';
    if (currentRecord.motorUnitPotentials?.riseTimeUs !== undefined && currentRecord.motorUnitPotentials.riseTimeUs < 0) errors.mup_riseTimeUs = 'Tiempo de elevación inválido.';
    if (!currentRecord.recruitmentPattern) errors.recruitmentPattern = 'Seleccione patrón de reclutamiento.';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addOrUpdateEmgRecord = () => {
    if (!validateCurrentRecord()) return;

    const newRecord: EMGNerveRecord = {
      id: editingRecordId || crypto.randomUUID(),
      muscleOrNerveName: currentRecord.muscleOrNerveName!,
      side: currentRecord.side!,
      insertionalActivity: currentRecord.insertionalActivity!,
      spontaneousActivity: currentRecord.spontaneousActivity!,
      motorUnitPotentials: currentRecord.motorUnitPotentials!,
      recruitmentPattern: currentRecord.recruitmentPattern!,
      maxVolitionalActivity: currentRecord.maxVolitionalActivity,
      rawTableRow: currentRecord.rawTableRow,
      extractionSource: currentRecord.extractionSource,
      interpretationNotes: currentRecord.interpretationNotes,
    };
    setEmgRecords(prev => {
      const updated = editingRecordId
        ? prev.map(record => record.id === editingRecordId ? newRecord : record)
        : [...prev, newRecord];
      // Run physiological validation on all EMG records
      runEmgValidation(updated);
      return updated;
    });
    setCurrentRecord(initialCurrentRecordState); // Reset form
    setEditingRecordId(null);
    setValidationErrors({});
  };

  const startEditingRecord = (record: EMGNerveRecord) => {
    setEditingRecordId(record.id);
    setCurrentRecord({
      ...record,
      spontaneousActivity: {
        fibrillations: record.spontaneousActivity.fibrillations,
        positiveWaves: record.spontaneousActivity.positiveWaves,
        fasciculations: record.spontaneousActivity.fasciculations,
        crd: record.spontaneousActivity.crd || false,
        myotonicDischarges: record.spontaneousActivity.myotonicDischarges || false,
      },
      motorUnitPotentials: {
        ...record.motorUnitPotentials,
      },
      maxVolitionalActivity: {
        amplitude: record.maxVolitionalActivity?.amplitude || '',
        pattern: record.maxVolitionalActivity?.pattern || '',
        effort: record.maxVolitionalActivity?.effort || '',
      }
    });
    setValidationErrors({});
  };

  const cancelEditingRecord = () => {
    setEditingRecordId(null);
    setCurrentRecord(initialCurrentRecordState);
    setValidationErrors({});
  };

  // Convert EMGNerveRecord to UnifiedEMGResults for validation
  const convertToUnifiedEMG = (record: EMGNerveRecord): UnifiedEMGResults => {
    // Map local insertionalActivity (includes 'absent') to UnifiedEMGResults (does not have 'absent')
    const mapInsertional = (val: string): 'normal' | 'increased' | 'decreased' => {
      if (val === 'increased') return 'increased';
      if (val === 'decreased' || val === 'absent') return 'decreased';
      return 'normal';
    };
    const maxPattern = record.maxVolitionalActivity?.pattern;
    const interferenceFromMax =
      maxPattern === 'full' || maxPattern === 'reduced' || maxPattern === 'discrete' || maxPattern === 'absent'
        ? maxPattern
        : undefined;

    return {
      muscle: getMuscleDisplayName(record.muscleOrNerveName),
      side: record.side,
      insertionalActivity: mapInsertional(record.insertionalActivity || 'normal'),
      spontaneousActivity: {
        fibrillations: record.spontaneousActivity.fibrillations > 0 ? `${record.spontaneousActivity.fibrillations}+` : 'none',
        positiveSharpWaves: record.spontaneousActivity.positiveWaves > 0 ? `${record.spontaneousActivity.positiveWaves}+` : 'none',
        fasciculations: record.spontaneousActivity.fasciculations,
        complexRepetitiveDischarges: record.spontaneousActivity.crd,
        myotonicDischarges: record.spontaneousActivity.myotonicDischarges,
      },
      motorUnitActionPotentials: {
        amplitude: record.motorUnitPotentials.amplitude,
        duration: record.motorUnitPotentials.duration,
        polyphasia: record.motorUnitPotentials.polyphasia,
        phases: record.motorUnitPotentials.phases,
        turns: record.motorUnitPotentials.turns,
      },
      recruitment: record.recruitmentPattern === 'reduced_incomplete' || 
                   record.recruitmentPattern === 'reduced_complete' 
                     ? 'reduced' 
                     : record.recruitmentPattern === 'early' ? 'early'
                     : record.recruitmentPattern === 'discrete' ? 'reduced'
                     : 'normal',
      interferencePattern: record.recruitmentPattern === 'early' ? 'full'
                          : record.recruitmentPattern === 'discrete' ? 'discrete'
                          : record.recruitmentPattern === 'normal' ? 'full'
                          : interferenceFromMax
                            ? interferenceFromMax
                            : record.maxVolitionalActivity?.amplitude === 'absent' ? 'absent' : 'reduced',
    } as unknown as UnifiedEMGResults;
  };

  const runEmgValidation = (records: EMGNerveRecord[]) => {
    const allAlerts: ValidationAlert[] = [];
    records.forEach(record => {
      const unified = convertToUnifiedEMG(record);
      allAlerts.push(...PhysiologicalValidationService.validateEMGData(unified));
      if ((record.motorUnitPotentials.riseTimeUs || 0) > 500) {
        allAlerts.push({
          type: 'suspicious',
          severity: 'warning',
          parameter: `${getMuscleDisplayName(record.muscleOrNerveName)} - Rise time`,
          value: `${record.motorUnitPotentials.riseTimeUs} µs`,
          expectedRange: '≤500 µs para PAUM cuantitativo',
          message: 'Tiempo de elevación >500 µs: el PAUM puede estar demasiado lejos de la aguja para medición cuantitativa confiable.',
          recommendation: 'Reubicar aguja o excluir ese PAUM de medidas cuantitativas.',
        });
      }
    });
    setEmgValidationAlerts(allAlerts);
  };

  const removeEmgRecord = (id: string) => {
    setEmgRecords(prev => {
      const updated = prev.filter(record => record.id !== id);
      runEmgValidation(updated);
      return updated;
    });
    if (editingRecordId === id) {
      cancelEditingRecord();
    }
  };

  React.useEffect(() => {
    if (currentRecord.muscleOrNerveName && currentRecord.motorUnitPotentials) {
      const tempRecord: EMGNerveRecord = {
        id: 'temp',
        muscleOrNerveName: currentRecord.muscleOrNerveName,
        side: currentRecord.side || 'right',
        insertionalActivity: currentRecord.insertionalActivity || 'normal',
        spontaneousActivity: currentRecord.spontaneousActivity || {
          fibrillations: 0,
          positiveWaves: 0,
          fasciculations: false,
          crd: false,
          myotonicDischarges: false
        },
        motorUnitPotentials: currentRecord.motorUnitPotentials,
        recruitmentPattern: currentRecord.recruitmentPattern || 'normal',
        interpretationNotes: ''
      };
      const unified = convertToUnifiedEMG(tempRecord);
      const alerts = PhysiologicalValidationService.validateEMGData(unified)
        .filter(a => a.severity === 'error' && a.message.length > 5);
      setCurrentEmgValidationAlerts(alerts);
    } else {
      setCurrentEmgValidationAlerts([]);
    }
  }, [currentRecord]);

  const handleSaveEvaluation = () => {
    if (emgRecords.length === 0) {
      setValidationErrors({ form: 'Debe agregar al menos un registro EMG.' });
      return;
    }
    onComplete(emgRecords);
  };
  
  const getMuscleDisplayName = (id: string) => emgMusclesDatabase.find(m => m.id === id)?.name || id;

  return (
    <div className="space-y-6">
      {validationErrors.form && (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-md flex items-center mb-4">
          <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-300 text-sm">{validationErrors.form}</span>
        </div>
      )}

      {/* Formulario para agregar nuevo registro EMG */}
      <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-gray-700/50 space-y-6">
        <h3 className="text-xl font-semibold text-gray-200">
          {editingRecordId ? 'Editar Registro EMG' : 'Agregar Registro EMG por Músculo/Nervio'}
        </h3>

        {/* Selección de Músculo y Lado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="muscleOrNerveName" className="block text-sm font-medium text-gray-300 mb-1">Músculo/Nervio</label>
            <select
              id="muscleOrNerveName"
              value={currentRecord.muscleOrNerveName}
              onChange={(e) => handleInputChange('muscleOrNerveName', e.target.value)}
              className={`w-full px-3 py-2 bg-gray-800 border ${validationErrors.muscleOrNerveName ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="">Seleccione un músculo/nervio...</option>
              {emgMusclesDatabase.map(muscle => (
                <option key={muscle.id} value={muscle.id}>{muscle.name}</option>
              ))}
            </select>
            {validationErrors.muscleOrNerveName && <p className="mt-1 text-sm text-red-400">{validationErrors.muscleOrNerveName}</p>}
            
            {/* ALERTAS FISIOLÓGICAS EN TIEMPO REAL */}
            {currentEmgValidationAlerts.length > 0 && (
              <div className="mt-2 text-xs p-2 bg-red-900/40 border border-red-500 rounded text-red-300">
                <span className="font-bold">⚠️ Imposibilidad Fisiológica:</span>
                <ul className="list-disc pl-4 mt-1">
                  {currentEmgValidationAlerts.map((alert, idx) => (
                    <li key={idx}>{alert.message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Lado</label>
            <div className="flex space-x-3">
              {(['left', 'right'] as const).map(sideOption => (
                <button
                  key={sideOption}
                  type="button"
                  onClick={() => handleInputChange('side', sideOption)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentRecord.side === sideOption 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {sideOption === 'left' ? 'Izquierdo' : 'Derecho'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actividad de Inserción */}
        <div>
          <label htmlFor="insertionalActivity" className="block text-sm font-medium text-gray-300 mb-1">Actividad de Inserción</label>
          <select
            id="insertionalActivity"
            value={currentRecord.insertionalActivity}
            onChange={(e) => handleInputChange('insertionalActivity', e.target.value as EMGNerveRecord['insertionalActivity'])}
            className={`w-full px-3 py-2 bg-gray-800 border ${validationErrors.insertionalActivity ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">Seleccione...</option>
            <option value="normal">Normal</option>
            <option value="increased">Aumentada</option>
            <option value="decreased">Disminuida</option>
            <option value="absent">Ausente</option>
          </select>
          {validationErrors.insertionalActivity && <p className="mt-1 text-sm text-red-400">{validationErrors.insertionalActivity}</p>}
        </div>

        {/* Actividad Espontánea */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-300 mb-1">Actividad Espontánea</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fibrillations" className="block text-xs font-medium text-gray-400 mb-1">Fibrilaciones (0-4+)</label>
              <select
                id="fibrillations"
                value={currentRecord.spontaneousActivity?.fibrillations ?? 0}
                onChange={(e) => handleNestedInputChange('spontaneousActivity', 'fibrillations', parseInt(e.target.value) as 0|1|2|3|4)}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[0, 1, 2, 3, 4].map(val => <option key={val} value={val}>{val === 0 ? '0 (Ausente)' : `${val}+`}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="positiveWaves" className="block text-xs font-medium text-gray-400 mb-1">Ondas Positivas (0-4+)</label>
              <select
                id="positiveWaves"
                value={currentRecord.spontaneousActivity?.positiveWaves ?? 0}
                onChange={(e) => handleNestedInputChange('spontaneousActivity', 'positiveWaves', parseInt(e.target.value) as 0|1|2|3|4)}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[0, 1, 2, 3, 4].map(val => <option key={val} value={val}>{val === 0 ? '0 (Ausente)' : `${val}+`}</option>)}
              </select>
            </div>
            <div className="col-span-1 md:col-span-2 flex space-x-6 mt-2">
               <label className="flex items-center space-x-2">
                 <input type="checkbox" checked={currentRecord.spontaneousActivity?.fasciculations || false} onChange={e => handleNestedInputChange('spontaneousActivity', 'fasciculations', e.target.checked)} className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500" />
                 <span className="text-gray-300 text-sm">Fasciculaciones</span>
               </label>
               <label className="flex items-center space-x-2">
                 <input type="checkbox" checked={currentRecord.spontaneousActivity?.crd || false} onChange={e => handleNestedInputChange('spontaneousActivity', 'crd', e.target.checked)} className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500" />
                 <span className="text-gray-300 text-sm">Descargas Repetitivas Complejas (CRDs)</span>
               </label>
               <label className="flex items-center space-x-2">
                 <input type="checkbox" checked={currentRecord.spontaneousActivity?.myotonicDischarges || false} onChange={e => handleNestedInputChange('spontaneousActivity', 'myotonicDischarges', e.target.checked)} className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500" />
                 <span className="text-gray-300 text-sm">Descargas Miotónicas</span>
               </label>
            </div>
          </div>
        </fieldset>

        {/* Potenciales de Unidad Motora (PUM) */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-gray-300 mb-1">Potenciales de Unidad Motora (PUM)</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="mup_amplitude" className="block text-xs font-medium text-gray-400 mb-0.5">Amplitud (µV)</label>
              <input
                type="number"
                id="mup_amplitude"
                value={currentRecord.motorUnitPotentials?.amplitude ?? ''}
                onChange={(e) => {
                  handleNestedInputChange('motorUnitPotentials', 'amplitude', parseFloat(e.target.value) || 0);
                  handleNestedInputChange('motorUnitPotentials', 'amplitudeLabel', '');
                }}
                className={`w-full px-3 py-2 text-sm bg-gray-800 border ${validationErrors.mup_amplitude ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {validationErrors.mup_amplitude && <p className="mt-1 text-xs text-red-400">{validationErrors.mup_amplitude}</p>}
              {currentRecord.motorUnitPotentials?.amplitudeLabel && <p className="mt-1 text-xs text-blue-300">Valor extraído: {currentRecord.motorUnitPotentials.amplitudeLabel}</p>}
            </div>
            <div>
              <label htmlFor="mup_duration" className="block text-xs font-medium text-gray-400 mb-0.5">Duración (ms)</label>
              <input
                type="number"
                id="mup_duration"
                value={currentRecord.motorUnitPotentials?.duration ?? ''}
                onChange={(e) => {
                  handleNestedInputChange('motorUnitPotentials', 'duration', parseFloat(e.target.value) || 0);
                  handleNestedInputChange('motorUnitPotentials', 'durationLabel', '');
                }}
                className={`w-full px-3 py-2 text-sm bg-gray-800 border ${validationErrors.mup_duration ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {validationErrors.mup_duration && <p className="mt-1 text-xs text-red-400">{validationErrors.mup_duration}</p>}
              {currentRecord.motorUnitPotentials?.durationLabel && <p className="mt-1 text-xs text-blue-300">Valor extraído: {currentRecord.motorUnitPotentials.durationLabel}</p>}
            </div>
            <div>
              <label htmlFor="mup_polyphasia" className="block text-xs font-medium text-gray-400 mb-0.5">Polifasia (%)</label>
              <input
                type="number"
                id="mup_polyphasia"
                min="0" max="100"
                value={currentRecord.motorUnitPotentials?.polyphasia ?? ''}
                onChange={(e) => {
                  handleNestedInputChange('motorUnitPotentials', 'polyphasia', parseFloat(e.target.value) || 0);
                  handleNestedInputChange('motorUnitPotentials', 'polyphasiaLabel', '');
                }}
                className={`w-full px-3 py-2 text-sm bg-gray-800 border ${validationErrors.mup_polyphasia ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {validationErrors.mup_polyphasia && <p className="mt-1 text-xs text-red-400">{validationErrors.mup_polyphasia}</p>}
              {currentRecord.motorUnitPotentials?.polyphasiaLabel && <p className="mt-1 text-xs text-blue-300">Valor extraído: {currentRecord.motorUnitPotentials.polyphasiaLabel}</p>}
            </div>
            <div>
              <label htmlFor="mup_configuration" className="block text-xs font-medium text-gray-400 mb-0.5">Configuración</label>
              <select
                id="mup_configuration"
                value={currentRecord.motorUnitPotentials?.configuration ?? ''}
                onChange={(e) => handleNestedInputChange('motorUnitPotentials', 'configuration', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No reportado</option>
                <option value="normal">Normal</option>
                <option value="few">Few / escasos</option>
                <option value="polyphasic">Polifásica</option>
                <option value="serrated">Serrada</option>
                <option value="unstable">Inestable</option>
                <option value="none">None</option>
              </select>
            </div>
            <div>
              <label htmlFor="mup_phases" className="block text-xs font-medium text-gray-400 mb-0.5">Fases (#)</label>
              <input
                type="number"
                id="mup_phases"
                min="0"
                value={currentRecord.motorUnitPotentials?.phases ?? ''}
                onChange={(e) => handleNestedInputChange('motorUnitPotentials', 'phases', parseFloat(e.target.value) || 0)}
                className={`w-full px-3 py-2 text-sm bg-gray-800 border ${validationErrors.mup_phases ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {validationErrors.mup_phases && <p className="mt-1 text-xs text-red-400">{validationErrors.mup_phases}</p>}
            </div>
            <div>
              <label htmlFor="mup_turns" className="block text-xs font-medium text-gray-400 mb-0.5">Giros / Turns (#)</label>
              <input
                type="number"
                id="mup_turns"
                min="0"
                value={currentRecord.motorUnitPotentials?.turns ?? ''}
                onChange={(e) => handleNestedInputChange('motorUnitPotentials', 'turns', parseFloat(e.target.value) || 0)}
                className={`w-full px-3 py-2 text-sm bg-gray-800 border ${validationErrors.mup_turns ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {validationErrors.mup_turns && <p className="mt-1 text-xs text-red-400">{validationErrors.mup_turns}</p>}
            </div>
            <div>
              <label htmlFor="mup_riseTimeUs" className="block text-xs font-medium text-gray-400 mb-0.5">Rise time (µs)</label>
              <input
                type="number"
                id="mup_riseTimeUs"
                min="0"
                value={currentRecord.motorUnitPotentials?.riseTimeUs ?? ''}
                onChange={(e) => handleNestedInputChange('motorUnitPotentials', 'riseTimeUs', parseFloat(e.target.value) || 0)}
                className={`w-full px-3 py-2 text-sm bg-gray-800 border ${validationErrors.mup_riseTimeUs ? 'border-red-500' : (currentRecord.motorUnitPotentials?.riseTimeUs || 0) > 500 ? 'border-yellow-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              {validationErrors.mup_riseTimeUs && <p className="mt-1 text-xs text-red-400">{validationErrors.mup_riseTimeUs}</p>}
              {(currentRecord.motorUnitPotentials?.riseTimeUs || 0) > 500 && <p className="mt-1 text-xs text-yellow-300">Puede no ser aceptable para cuantificación.</p>}
            </div>
          </div>
        </fieldset>

        {/* Máxima Actividad Voluntaria */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-gray-300 mb-1">Máxima Actividad Voluntaria</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="maxVolitionalAmplitude" className="block text-xs font-medium text-gray-400 mb-0.5">Amplitud</label>
              <select
                id="maxVolitionalAmplitude"
                value={currentRecord.maxVolitionalActivity?.amplitude ?? ''}
                onChange={(e) => handleInputChange('maxVolitionalActivity', {
                  ...(currentRecord.maxVolitionalActivity || {}),
                  amplitude: e.target.value as EMGMaxVolitionalActivity['amplitude']
                })}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No reportado</option>
                <option value="normal">Normal</option>
                <option value="reduced">Reducida</option>
                <option value="increased">Aumentada</option>
                <option value="absent">Ausente</option>
              </select>
            </div>
            <div>
              <label htmlFor="maxVolitionalPattern" className="block text-xs font-medium text-gray-400 mb-0.5">Patrón</label>
              <select
                id="maxVolitionalPattern"
                value={currentRecord.maxVolitionalActivity?.pattern ?? ''}
                onChange={(e) => handleInputChange('maxVolitionalActivity', {
                  ...(currentRecord.maxVolitionalActivity || {}),
                  pattern: e.target.value as EMGMaxVolitionalActivity['pattern']
                })}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No reportado</option>
                <option value="full">Full / completo</option>
                <option value="reduced">Reducido</option>
                <option value="discrete">Discreto</option>
                <option value="early">Precoz</option>
                <option value="absent">Ausente</option>
              </select>
            </div>
            <div>
              <label htmlFor="maxVolitionalEffort" className="block text-xs font-medium text-gray-400 mb-0.5">Esfuerzo</label>
              <select
                id="maxVolitionalEffort"
                value={currentRecord.maxVolitionalActivity?.effort ?? ''}
                onChange={(e) => handleInputChange('maxVolitionalActivity', {
                  ...(currentRecord.maxVolitionalActivity || {}),
                  effort: e.target.value as EMGMaxVolitionalActivity['effort']
                })}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No reportado</option>
                <option value="max">Max.</option>
                <option value="submax">Sub Max.</option>
                <option value="poor">Pobre</option>
              </select>
            </div>
          </div>
        </fieldset>

        {/* Patrón de Reclutamiento */}
        <div>
          <label htmlFor="recruitmentPattern" className="block text-sm font-medium text-gray-300 mb-1">Patrón de Reclutamiento</label>
          <select
            id="recruitmentPattern"
            value={currentRecord.recruitmentPattern}
            onChange={(e) => handleInputChange('recruitmentPattern', e.target.value as EMGNerveRecord['recruitmentPattern'])}
            className={`w-full px-3 py-2 bg-gray-800 border ${validationErrors.recruitmentPattern ? 'border-red-500' : 'border-gray-700'} text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">Seleccione...</option>
            <option value="normal">Normal</option>
            <option value="reduced_incomplete">Reducido (incompleto)</option>
            <option value="reduced_complete">Reducido (completo)</option>
            <option value="early">Precoz</option>
            <option value="discrete">Discreto</option>
          </select>
          {validationErrors.recruitmentPattern && <p className="mt-1 text-sm text-red-400">{validationErrors.recruitmentPattern}</p>}
        </div>
        
        {/* Notas de Interpretación */}
        <div>
            <label htmlFor="interpretationNotes" className="block text-sm font-medium text-gray-300 mb-1">Notas de Interpretación (Opcional)</label>
            <textarea
                id="interpretationNotes"
                value={currentRecord.interpretationNotes || ''}
                onChange={(e) => handleInputChange('interpretationNotes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Anotaciones específicas para este músculo/nervio..."
            />
        </div>

        <div className="flex justify-end gap-3">
          {editingRecordId && (
            <button
              type="button"
              onClick={cancelEditingRecord}
              className="flex items-center px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-gray-500 transition-colors"
            >
              <X className="mr-2 h-4 w-4" />
              Cancelar edición
            </button>
          )}
          <button
            type="button"
            onClick={addOrUpdateEmgRecord}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-blue-500 transition-colors"
          >
            {editingRecordId ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
            {editingRecordId ? 'Guardar Cambios' : 'Agregar Registro EMG'}
          </button>
        </div>
      </div>

      {/* Lista de Registros EMG Agregados */}
      {emgRecords.length > 0 && (
        <div className="mt-8 bg-gray-800/30 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-gray-700/40">
          <h3 className="text-lg font-semibold text-gray-200 mb-4">Registros EMG Guardados</h3>
          <div className="space-y-4">
            {emgRecords.map((record) => (
              <div key={record.id} className="bg-gray-700/50 p-4 rounded-md border border-gray-600">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-gray-100">
                    {getMuscleDisplayName(record.muscleOrNerveName)} ({record.side === 'left' ? 'Izq.' : 'Der.'})
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startEditingRecord(record)}
                      className="p-1 text-blue-300 hover:text-blue-200 transition-colors"
                      aria-label="Editar registro"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeEmgRecord(record.id)}
                      className="p-1 text-red-400 hover:text-red-300 transition-colors"
                      aria-label="Eliminar registro"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                  <p><strong className="text-gray-400">Inserción:</strong> <span className="text-gray-200">{record.insertionalActivity}</span></p>
                  <p><strong className="text-gray-400">Reclutamiento:</strong> <span className="text-gray-200">{record.recruitmentPattern}</span></p>
                  <div>
                    <strong className="text-gray-400">Act. Espontánea:</strong>
                    <ul className="list-disc list-inside ml-1">
                      {record.spontaneousActivity.fibrillations > 0 && <li className="text-gray-300">Fibr.: {record.spontaneousActivity.fibrillations}+</li>}
                      {record.spontaneousActivity.positiveWaves > 0 && <li className="text-gray-300">O.P.: {record.spontaneousActivity.positiveWaves}+</li>}
                      {record.spontaneousActivity.fasciculations && <li className="text-gray-300">Fasciculaciones</li>}
                      {record.spontaneousActivity.crd && <li className="text-gray-300">CRDs</li>}
                      {record.spontaneousActivity.myotonicDischarges && <li className="text-gray-300">Descargas miotónicas</li>}
                      {!(record.spontaneousActivity.fibrillations > 0 || record.spontaneousActivity.positiveWaves > 0 || record.spontaneousActivity.fasciculations || record.spontaneousActivity.crd || record.spontaneousActivity.myotonicDischarges) && <li className="text-gray-500">Ninguna</li>}
                    </ul>
                  </div>
                  <div>
                    <strong className="text-gray-400">PUM Amplitud:</strong> <span className="text-gray-200">{formatMupValue(record.motorUnitPotentials.amplitude, record.motorUnitPotentials.amplitudeLabel, 'µV')}</span>
                  </div>
                  <div>
                    <strong className="text-gray-400">PUM Duración:</strong> <span className="text-gray-200">{formatMupValue(record.motorUnitPotentials.duration, record.motorUnitPotentials.durationLabel, 'ms')}</span>
                  </div>
                  <div>
                    <strong className="text-gray-400">PUM Polifasia:</strong> <span className="text-gray-200">{formatMupValue(record.motorUnitPotentials.polyphasia, record.motorUnitPotentials.polyphasiaLabel, '%')}</span>
                  </div>
                  {record.motorUnitPotentials.configuration && (
                    <div>
                      <strong className="text-gray-400">PUM Config:</strong> <span className="text-gray-200">{record.motorUnitPotentials.configuration}</span>
                    </div>
                  )}
                  {record.motorUnitPotentials.phases !== undefined && (
                    <div>
                      <strong className="text-gray-400">Fases:</strong> <span className="text-gray-200">{record.motorUnitPotentials.phases}</span>
                    </div>
                  )}
                  {record.motorUnitPotentials.turns !== undefined && (
                    <div>
                      <strong className="text-gray-400">Turns:</strong> <span className="text-gray-200">{record.motorUnitPotentials.turns}</span>
                    </div>
                  )}
                  {record.motorUnitPotentials.riseTimeUs !== undefined && (
                    <div>
                      <strong className="text-gray-400">Rise time:</strong> <span className={(record.motorUnitPotentials.riseTimeUs || 0) > 500 ? 'text-yellow-300 font-semibold' : 'text-gray-200'}>{record.motorUnitPotentials.riseTimeUs} µs</span>
                    </div>
                  )}
                  {(record.maxVolitionalActivity?.amplitude || record.maxVolitionalActivity?.pattern || record.maxVolitionalActivity?.effort) && (
                    <div className="sm:col-span-2 md:col-span-3">
                      <strong className="text-gray-400">Máx. actividad voluntaria:</strong>{' '}
                      <span className="text-gray-200">
                        {[
                          record.maxVolitionalActivity?.amplitude && `Amplitud ${record.maxVolitionalActivity.amplitude}`,
                          record.maxVolitionalActivity?.pattern && `Patrón ${record.maxVolitionalActivity.pattern}`,
                          record.maxVolitionalActivity?.effort && `Esfuerzo ${record.maxVolitionalActivity.effort}`
                        ].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  )}
                  {record.rawTableRow && record.rawTableRow.length > 0 && (
                    <div className="sm:col-span-2 md:col-span-3">
                      <strong className="text-gray-400">Fila original:</strong>{' '}
                      <span className="text-gray-300">{record.rawTableRow.join(' | ')}</span>
                    </div>
                  )}
                </div>
                {record.interpretationNotes && (
                    <p className="text-sm mt-2"><strong className="text-gray-400">Notas:</strong> <span className="text-gray-300">{record.interpretationNotes}</span></p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* EMG Physiological Validation Alerts */}
      {emgValidationAlerts.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-semibold text-yellow-300 flex items-center">
            <ShieldAlert className="h-4 w-4 mr-2" />
            Alertas de Validación EMG
          </h4>
          {emgValidationAlerts.map((alert, index) => (
            <div 
              key={index} 
              className={`p-3 rounded-md border text-sm flex items-start ${
                alert.severity === 'error' 
                  ? 'bg-red-900/30 border-red-700/50' 
                  : alert.severity === 'warning'
                  ? 'bg-yellow-900/30 border-yellow-700/50'
                  : 'bg-blue-900/30 border-blue-700/50'
              }`}
            >
              {alert.severity === 'error' ? (
                <AlertTriangle className="h-4 w-4 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
              ) : alert.severity === 'warning' ? (
                <ShieldAlert className="h-4 w-4 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
              ) : (
                <Info className="h-4 w-4 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
              )}
              <div>
                <p className={`font-medium ${
                  alert.severity === 'error' ? 'text-red-300' : 
                  alert.severity === 'warning' ? 'text-yellow-300' : 'text-blue-300'
                }`}>
                  {alert.type === 'impossible' ? '🚫' : alert.type === 'suspicious' ? '⚠️' : '📝'}{' '}
                  {alert.parameter}
                </p>
                <p className="text-gray-300 mt-0.5">{alert.message}</p>
                {alert.recommendation && (
                  <p className="text-gray-400 text-xs mt-1 italic">💡 {alert.recommendation}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Botón de Guardar Evaluación Completa */}
      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={handleSaveEvaluation}
          disabled={emgRecords.length === 0}
          className="flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-green-500 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="mr-2 h-5 w-5" />
          Guardar Evaluación EMG Completa
        </button>
      </div>
    </div>
  );
};

export default EMGNeedleAnalysis; 